import { useEffect, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { gsap } from 'gsap';
import './feedback-character.css';

const FeedbackCharacter = ({ isActive = false, lookUp = false }) => {
  const eyesRef = useRef([]);
  const shapesRef = useRef([]);
  const uniqueId = useMemo(() => Math.random().toString(36).substr(2, 9), []);

  useEffect(() => {
    const eyes = eyesRef.current;
    if (!eyes.length) return;

    // When lookUp is true (user interacting), look up
    // Otherwise, keep eyes centered (looking straight)
    const targetX = 0;
    const targetY = lookUp ? -4 : 0;

    eyes.forEach((eye, i) => {
      gsap.to(eye, {
        x: targetX,
        y: targetY,
        duration: 0.3,
        ease: 'power2.out',
        delay: i * 0.05,
      });
    });

    // Add slight tilt to rectangles when active
    shapesRef.current.forEach((shape, i) => {
      gsap.to(shape, {
        rotationZ: isActive ? (i % 2 === 0 ? 2 : -2) : 0,
        scale: isActive ? 1.02 : 1,
        duration: 0.3,
        ease: 'power2.out',
      });
    });
  }, [isActive, lookUp]);

  return (
    <div className="feedback-character">
      <svg viewBox="0 0 400 500" className="character-svg">
        <defs>
          {/* Gradients for each rectangle */}
          <linearGradient id={`rect1Gradient-${uniqueId}`} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#ff6bcb" />
            <stop offset="100%" stopColor="#ff1493" />
          </linearGradient>
          <linearGradient id={`rect2Gradient-${uniqueId}`} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#a78bfa" />
            <stop offset="100%" stopColor="#8b5cf6" />
          </linearGradient>
          <linearGradient id={`rect3Gradient-${uniqueId}`} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#60a5fa" />
            <stop offset="100%" stopColor="#3b82f6" />
          </linearGradient>
          {/* Glow filter */}
          <filter id={`glow-${uniqueId}`}>
            <feGaussianBlur stdDeviation="4" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>

        {/* Rectangle 1 (Left - Thin & Short, Pink) - Slanted left */}
        <g ref={(el) => (shapesRef.current[0] = el)} className="shape float-1">
          <path
            d="M 60 260 L 75 220 L 95 220 L 110 260 L 100 440 L 70 440 Z"
            fill={`url(#rect1Gradient-${uniqueId})`}
            filter={`url(#glow-${uniqueId})`}
            stroke="rgba(255,255,255,0.3)"
            strokeWidth="2"
          />
          {/* Eyes */}
          <circle cx="75" cy="310" r="7" fill="white" opacity="0.95" />
          <circle ref={(el) => (eyesRef.current[0] = el)} cx="75" cy="310" r="3.5" fill="#1a1a2e" className="pupil" />
          <circle cx="95" cy="310" r="7" fill="white" opacity="0.95" />
          <circle ref={(el) => (eyesRef.current[1] = el)} cx="95" cy="310" r="3.5" fill="#1a1a2e" className="pupil" />
          {/* Sparkle */}
          <circle cx="90" cy="245" r="2" fill="white" opacity="0.8">
            <animate attributeName="opacity" values="0.3;1;0.3" dur="2s" repeatCount="indefinite" />
          </circle>
        </g>

        {/* Rectangle 2 (Center - Wide & Tallest, Purple) - Slanted right */}
        <g ref={(el) => (shapesRef.current[1] = el)} className="shape float-2">
          <path
            d="M 100 440 L 90 140 L 120 100 L 170 100 L 200 140 L 190 440 Z"
            fill={`url(#rect2Gradient-${uniqueId})`}
            filter={`url(#glow-${uniqueId})`}
            stroke="rgba(255,255,255,0.3)"
            strokeWidth="2.5"
          />
          {/* Eyes */}
          <circle cx="120" cy="250" r="11" fill="white" opacity="0.95" />
          <circle ref={(el) => (eyesRef.current[2] = el)} cx="120" cy="250" r="5.5" fill="#1a1a2e" className="pupil" />
          <circle cx="170" cy="250" r="11" fill="white" opacity="0.95" />
          <circle ref={(el) => (eyesRef.current[3] = el)} cx="170" cy="250" r="5.5" fill="#1a1a2e" className="pupil" />
          {/* Sparkle */}
          <circle cx="155" cy="160" r="2.5" fill="white" opacity="0.8">
            <animate attributeName="opacity" values="0.3;1;0.3" dur="2.5s" repeatCount="indefinite" />
          </circle>
        </g>

        {/* Rectangle 3 (Right - Medium width & height, Blue) - Slanted left */}
        <g ref={(el) => (shapesRef.current[2] = el)} className="shape float-3">
          <path
            d="M 190 440 L 200 200 L 220 160 L 250 160 L 270 200 L 260 440 Z"
            fill={`url(#rect3Gradient-${uniqueId})`}
            filter={`url(#glow-${uniqueId})`}
            stroke="rgba(255,255,255,0.3)"
            strokeWidth="2"
          />
          {/* Eyes */}
          <circle cx="210" cy="280" r="9" fill="white" opacity="0.95" />
          <circle ref={(el) => (eyesRef.current[4] = el)} cx="210" cy="280" r="4.5" fill="#1a1a2e" className="pupil" />
          <circle cx="245" cy="280" r="9" fill="white" opacity="0.95" />
          <circle ref={(el) => (eyesRef.current[5] = el)} cx="245" cy="280" r="4.5" fill="#1a1a2e" className="pupil" />
          {/* Sparkle */}
          <circle cx="235" cy="195" r="2" fill="white" opacity="0.8">
            <animate attributeName="opacity" values="0.3;1;0.3" dur="3s" repeatCount="indefinite" />
          </circle>
        </g>
      </svg>
    </div>
  );
};

FeedbackCharacter.propTypes = {
  isActive: PropTypes.bool,
  lookUp: PropTypes.bool,
};

export default FeedbackCharacter;